import { UserButton } from "@clerk/clerk-react";
import { useRouterState } from "@tanstack/react-router";
import { Search } from "lucide-react";

import { setColorScheme, useColorScheme } from "../../design/theme-provider.js";
import { isLocalLabAuthMode } from "../../lib/auth-mode.js";
import { hideStaffKnowledgePath } from "../../lib/staff-header-path.js";
import { useOptionalCapture } from "../capture/capture-provider.js";
import { useCommandPalette } from "../command-palette/command-palette.js";
import { useHelp } from "../help/help-context.js";
import { TimerWidget } from "../time-tracking/timer-widget.js";

import { MobileNav } from "./mobile-nav.js";
import { SHOW_LEFT_NAV } from "./shell-flags.js";
import { StaffDemoPath, StaffTenantSelect } from "./staff-tenant-select.js";

// Map of route → display title for the page-title slot. Anything not
// listed falls through to a humanised version of the path.
const TITLES: Record<string, string> = {
  "/": "Insights",
  "/subject": "Subject",
  "/dashboard": "Dashboard",
  "/projects": "Projects",
  "/tasks": "My tasks",
  "/contacts": "Contacts",
  "/companies": "Companies",
  "/documents": "Documents",
  "/my-vault": "My vault",
  "/board-meetings": "Board",
  "/signatures": "Signatures",
  "/email": "Tracked Email",
  "/authenticator": "Authenticator",
  "/cashflow": "Cashflow",
  "/receipts": "Receipts",
  "/meetings": "Meetings",
  "/messages": "Messages",
  "/notes": "Notes",
  "/ideation/chat": "Ideation Chat",
  "/ideation/maps": "Atlas",
  "/ideation": "Ideation",
  "/scheduling": "Scheduling",
  "/time-tracking": "Time tracking",
  "/partner-vaults": "Partner vaults",
  "/portal-users/new": "New partner portal",
  "/portal-users": "Portal users",
  "/engagement-plan": "Engagement plan",
  "/settings/tokens": "Personal Access Tokens",
  "/settings": "Settings & Usage",
};

function titleFor(path: string): string {
  const exact = TITLES[path];
  if (exact) return exact;
  for (const [prefix, title] of Object.entries(TITLES)) {
    if (path.startsWith(prefix + "/")) return title;
  }
  // Fallback: take the first segment, prettify
  const seg = path.split("/").filter(Boolean)[0];
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : "Insights";
}

function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
}

export function Header() {
  const { openHelp } = useHelp();
  const { toggle } = useCommandPalette();
  const capture = useOptionalCapture();
  const colorScheme = useColorScheme();
  const nextScheme = colorScheme === "dark" ? "light" : "dark";
  const { location } = useRouterState();
  const path = location.pathname;
  const title = titleFor(path);
  const shortcut = isMacPlatform() ? "⌘K" : "Ctrl+K";
  const hideModulePath = hideStaffKnowledgePath(path);

  return (
    <header
      className={
        hideModulePath
          ? "flex min-h-16 flex-wrap items-center justify-start gap-x-3 gap-y-2 border-b border bg-surface px-3 py-2 md:px-6"
          : "flex min-h-16 flex-wrap items-center justify-between gap-2 border-b border bg-surface px-3 py-2 md:px-6"
      }
    >
      <div className="flex items-center gap-2">
        {/* Hamburger opens the mobile nav drawer when the old left-nav is shown.
            Otherwise the desktop rail is primary and Jump to… covers mobile. */}
        {SHOW_LEFT_NAV ? <MobileNav /> : null}
        <h1 className="text-xl font-bold tracking-tight text-(--color-brand-graphite)">{title}</h1>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <StaffTenantSelect />
        {hideModulePath ? null : <StaffDemoPath />}
        {capture ? (
          <button
            type="button"
            onClick={() => capture.open()}
            className="inline-flex items-center rounded-soft-md border border-border bg-surface px-3 py-1.5 text-sm text-fg transition-colors hover:bg-surface-muted"
          >
            Capture
          </button>
        ) : null}
        <button
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-2 rounded-soft-md border border-border bg-surface px-3 py-1.5 text-sm text-fg-subtle transition-colors hover:bg-surface-muted hover:text-fg"
          title={`Open command palette (${shortcut})`}
          aria-label={`Open command palette (${shortcut})`}
        >
          <Search className="size-4" aria-hidden="true" />
          <span className="sm:inline">Jump to…</span>
          <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-fg-muted">
            {shortcut}
          </kbd>
        </button>
        <TimerWidget />
        <button
          type="button"
          onClick={() => setColorScheme(nextScheme)}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-fg-subtle hover:bg-border-muted hover:text-fg"
          title={nextScheme === "dark" ? "Use dark theme" : "Use light theme"}
        >
          {nextScheme === "dark" ? "Dark" : "Light"}
        </button>
        <button
          onClick={openHelp}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-(--color-brand-teal) hover:bg-border-muted"
          title="Help"
        >
          Help
        </button>
        {isLocalLabAuthMode ? (
          <span className="rounded-full border border-border-muted px-3 py-1 text-xs font-medium text-fg-subtle">
            Local lab
          </span>
        ) : (
          <UserButton afterSignOutUrl="/sign-in" />
        )}
      </div>
    </header>
  );
}
